import type * as React from 'react'
import { forwardRef } from 'react'

import { clsx } from 'clsx'

import { Slot } from '@buffer-mono/popcorn'

import styles from './Card.module.css'

export interface HomeCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Render as the single child element instead of a wrapping div, merging the
   * card styles onto it (Radix `Slot`).
   */
  asChild?: boolean
  /**
   * Corner radius.
   * @default 'md'
   */
  radius?: 'md' | 'lg'
}

/**
 * Shared surface for Buffer Home cards and containers.
 *
 * Implements the Home card spec (CORE-5795): `background/muted` in light mode,
 * `background/raised` in dark mode, with a `border/surface/raised` border.
 */
export const Card = forwardRef<HTMLDivElement, HomeCardProps>(function Card(
  { asChild, radius = 'md', className, ...props },
  ref,
) {
  const Tag = asChild ? Slot.Root : 'div'

  return (
    <Tag
      ref={ref}
      className={clsx(
        styles.card,
        radius === 'lg' && styles.radiusLg,
        className,
      )}
      {...props}
    />
  )
})

Card.displayName = 'HomeCard'
